import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileText, CheckCircle, AlertCircle,
  ArrowRight, ChevronDown, Loader2,
} from 'lucide-react';
import { apiRequest, API_BASE_URL } from '../../config/api';
import { useDataObject } from '../DataObjectSlugResolver';

/* ─────────────────────────────────────────────────────────────────
   Utility helpers
───────────────────────────────────────────────────────────────────*/

function parseCsvHeaders(text) {
  // Parse just the first line to extract column headers
  const firstLine = text.split(/\r?\n/)[0];
  if (!firstLine) return [];
  // Handle quoted fields
  const result = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < firstLine.length; i++) {
    const ch = firstLine[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(field.trim());
      field = '';
    } else {
      field += ch;
    }
  }
  result.push(field.trim());
  return result;
}

function parseCsvPreview(text, maxRows = 3) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = parseCsvHeaders(lines[0]);
  const rows = [];
  for (let i = 1; i <= Math.min(maxRows, lines.length - 1); i++) {
    const cols = [];
    let field = '';
    let inQuotes = false;
    for (let j = 0; j < lines[i].length; j++) {
      const ch = lines[i][j];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { cols.push(field.trim()); field = ''; }
      else { field += ch; }
    }
    cols.push(field.trim());
    const rowObj = {};
    headers.forEach((h, idx) => { rowObj[h] = cols[idx] || ''; });
    rows.push(rowObj);
  }
  return rows;
}

/* ─────────────────────────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────────────────────────────*/

function StepIndicator({ steps, currentStep }) {
  return (
    <div className="flex items-center gap-0">
      {steps.map((step, idx) => (
        <React.Fragment key={step.id}>
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                idx < currentStep
                  ? 'bg-[#C6F135] text-[#0B1120]'
                  : idx === currentStep
                  ? 'bg-[#0B1120] text-white'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {idx < currentStep ? <CheckCircle className="w-4 h-4" /> : idx + 1}
            </div>
            <span
              className={`text-sm font-medium ${
                idx === currentStep ? 'text-gray-900' : idx < currentStep ? 'text-gray-500' : 'text-gray-400'
              }`}
            >
              {step.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div className={`flex-1 h-px mx-3 min-w-[32px] ${idx < currentStep ? 'bg-[#C6F135]' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function DropZone({ onFile, file }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith('.csv') || f.type === 'text/csv')) onFile(f);
  }, [onFile]);

  const handleChange = (e) => {
    const f = e.target.files[0];
    if (f) onFile(f);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors py-12 px-6 ${
        dragging
          ? 'border-[#C6F135] bg-[#C6F135]/5'
          : file
          ? 'border-gray-300 bg-gray-50'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleChange} />
      {file ? (
        <>
          <FileText className="w-10 h-10 text-gray-400" />
          <div className="text-center">
            <p className="text-sm font-medium text-gray-900">{file.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
          <p className="text-xs text-gray-400">Click to replace</p>
        </>
      ) : (
        <>
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
            <Upload className="w-6 h-6 text-gray-400" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-gray-700">Drop your CSV file here</p>
            <p className="text-xs text-gray-500 mt-1">or click to browse &mdash; .csv files only</p>
          </div>
        </>
      )}
    </div>
  );
}

function MappingRow({ csvHeader, fieldDefs, value, onChange, previewValues }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selectedField = fieldDefs.find((f) => f.slug === value);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center py-3 border-b border-gray-100 last:border-0">
      {/* CSV column */}
      <div>
        <p className="text-sm font-medium text-gray-900">{csvHeader}</p>
        {previewValues?.length > 0 && (
          <p className="text-xs text-gray-400 mt-0.5 truncate">{previewValues.filter(Boolean).join(', ')}</p>
        )}
      </div>

      <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />

      {/* Field selector */}
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 hover:border-gray-300 bg-white transition-colors"
        >
          <span className={selectedField ? 'text-gray-900' : 'text-gray-400'}>
            {selectedField ? selectedField.name : 'Skip this column'}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        </button>
        {open && (
          <div className="absolute top-full left-0 right-0 mt-1 z-20 bg-white rounded-xl shadow-lg border border-gray-200 max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50"
            >
              Skip this column
            </button>
            {fieldDefs.map((fd) => (
              <button
                key={fd.slug}
                type="button"
                onClick={() => { onChange(fd.slug); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-gray-50"
              >
                <span>{fd.name}</span>
                <span className="ml-2 text-xs text-gray-400">{fd.field_type}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Main page
───────────────────────────────────────────────────────────────────*/

const STEPS = [
  { id: 'upload', label: 'Upload file' },
  { id: 'mapping', label: 'Map columns' },
  { id: 'settings', label: 'Import settings' },
  { id: 'results', label: 'Done' },
];

const MODE_OPTIONS = [
  {
    value: 'insert',
    label: 'Insert',
    description: 'Create a new record for every row. Duplicates are allowed.',
  },
  {
    value: 'upsert',
    label: 'Upsert',
    description: 'Update an existing record if the key field matches, otherwise insert.',
  },
  {
    value: 'skip_duplicates',
    label: 'Skip duplicates',
    description: 'Skip rows where the key field already exists in another record.',
  },
];

export default function UploadRecords() {
  const dataObject = useDataObject();
  const navigate = useNavigate();

  const objectName = dataObject?.name || 'Records';
  const objectSlug = dataObject?.slug_plural || dataObject?.slug;

  // Fetch field definitions for this object
  const [fieldDefs, setFieldDefs] = useState([]);
  const [fieldDefsLoading, setFieldDefsLoading] = useState(true);

  useEffect(() => {
    if (!dataObject?.id) return;
    setFieldDefsLoading(true);
    apiRequest(`/crm/objects/${dataObject.id}`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.field_definitions) {
          setFieldDefs(
            data.field_definitions.filter(
              (fd) => fd.field_type !== 'object_relationship' && fd.field_type !== 'user'
            )
          );
        }
      })
      .catch(() => {})
      .finally(() => setFieldDefsLoading(false));
  }, [dataObject?.id]);

  // Step state
  const [step, setStep] = useState(0); // 0=upload, 1=mapping, 2=settings, 3=results

  // Step 1: file
  const [file, setFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvDataRowCount, setCsvDataRowCount] = useState(0);

  // Step 2: column mapping  { csvHeader: fieldSlug | '' }
  const [mapping, setMapping] = useState({});

  // Step 3: import settings
  const [mode, setMode] = useState('insert');
  const [upsertField, setUpsertField] = useState('');

  // Step 4: results
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [importError, setImportError] = useState('');

  /* ── Handlers ── */

  const handleFile = useCallback((f) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const headers = parseCsvHeaders(text);
      setCsvHeaders(headers);
      const preview = parseCsvPreview(text);
      setCsvPreview(preview);
      // Count data rows using the same split logic as parseCsvPreview so the
      // displayed count matches what the backend's csv.DictReader will process.
      const dataLines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
      setCsvDataRowCount(Math.max(0, dataLines.length - 1));

      // Auto-map headers to field slugs by normalising names
      const autoMap = {};
      headers.forEach((h) => {
        const normalised = h.toLowerCase().replace(/[^a-z0-9]/g, '_');
        const match = fieldDefs.find(
          (fd) => fd.slug === normalised || fd.name.toLowerCase() === h.toLowerCase()
        );
        autoMap[h] = match ? match.slug : '';
      });
      setMapping(autoMap);
    };
    reader.readAsText(f);
  }, [fieldDefs]);

  const handleImport = async () => {
    if (!file || !dataObject) return;
    setImporting(true);
    setImportError('');
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ mode });
      if ((mode === 'upsert' || mode === 'skip_duplicates') && upsertField) {
        params.set('upsert_field', upsertField);
      }
      // Only include non-empty mappings
      const cleanMapping = {};
      Object.entries(mapping).forEach(([k, v]) => { if (v) cleanMapping[k] = v; });
      if (Object.keys(cleanMapping).length > 0) {
        params.set('column_mapping', JSON.stringify(cleanMapping));
      }

      const res = await fetch(
        `${API_BASE_URL}/crm/objects/${dataObject.id}/records/import?${params.toString()}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setResults(data);
      setStep(3);
    } catch (err) {
      setImportError(err.message || 'Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  /* ── Render helpers ── */

  const canProceedToMapping = file && csvHeaders.length > 0;
  const canProceedToSettings = Object.values(mapping).some(Boolean);
  const needsUpsertField = mode === 'upsert' || mode === 'skip_duplicates';
  const canImport = !needsUpsertField || upsertField;

  const mappedFieldSlugs = Object.values(mapping).filter(Boolean);

  if (fieldDefsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back link */}
      <button
        onClick={() => {
          if (step === 0 || step === 3) navigate(`/data/${objectSlug}`);
          else setStep(step - 1);
        }}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {step === 0 || step === 3 ? `Back to ${objectName}` : 'Back'}
      </button>

      {/* Title */}
      <div className="mb-8">
        <h1 className="font-['Satoshi'] text-2xl font-bold text-gray-900 tracking-tight">
          Upload records
        </h1>
        <p className="mt-1.5 text-gray-500 text-sm">
          Import {objectName.toLowerCase()} from a CSV file into your workspace.
        </p>
      </div>

      {/* Step indicator */}
      <div className="mb-8">
        <StepIndicator steps={STEPS} currentStep={step} />
      </div>

      {/* ── Step 0: Upload ── */}
      {step === 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Choose a CSV file</h2>
          <p className="text-sm text-gray-500 mb-5">
            The first row should contain column headers. UTF-8 encoding recommended.
          </p>
          <DropZone onFile={handleFile} file={file} />

          {csvHeaders.length > 0 && (
            <div className="mt-5 p-4 bg-gray-50 rounded-xl">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Preview</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      {csvHeaders.map((h) => (
                        <th key={h} className="text-left pr-4 pb-2 font-medium text-gray-700 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.map((row, i) => (
                      <tr key={i}>
                        {csvHeaders.map((h) => (
                          <td key={h} className="pr-4 py-1 text-gray-500 truncate max-w-[120px]">{row[h]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {csvDataRowCount} data rows detected
              </p>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              disabled={!canProceedToMapping}
              onClick={() => setStep(1)}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-[#0B1120] text-white rounded-lg hover:bg-[#1a2640] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 1: Column Mapping ── */}
      {step === 1 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Map columns</h2>
          <p className="text-sm text-gray-500 mb-5">
            Match each CSV column to a field in <strong>{objectName}</strong>. Unmapped columns will be skipped.
          </p>

          <div className="mb-2 grid grid-cols-[1fr_auto_1fr] gap-3">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">CSV column</p>
            <div />
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Object field</p>
          </div>

          {csvHeaders.map((header) => (
            <MappingRow
              key={header}
              csvHeader={header}
              fieldDefs={fieldDefs}
              value={mapping[header] || ''}
              onChange={(val) => setMapping((prev) => ({ ...prev, [header]: val }))}
              previewValues={csvPreview.map((row) => row[header]).filter(Boolean)}
            />
          ))}

          <div className="mt-6 flex justify-between">
            <button
              onClick={() => setStep(0)}
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              Back
            </button>
            <button
              disabled={!canProceedToSettings}
              onClick={() => setStep(2)}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-[#0B1120] text-white rounded-lg hover:bg-[#1a2640] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Settings ── */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Import settings</h2>
            <p className="text-sm text-gray-500 mb-5">
              Choose how to handle existing records.
            </p>

            <div className="space-y-3">
              {MODE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                    mode === opt.value
                      ? 'border-[#0B1120] bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="mode"
                    value={opt.value}
                    checked={mode === opt.value}
                    onChange={() => { setMode(opt.value); setUpsertField(''); }}
                    className="mt-0.5 accent-[#0B1120]"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>

            {needsUpsertField && (
              <div className="mt-5">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Unique key field
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Records will be matched by this field&apos;s value to detect duplicates.
                </p>
                <select
                  value={upsertField}
                  onChange={(e) => setUpsertField(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                >
                  <option value="">Select a field…</option>
                  {mappedFieldSlugs.map((slug) => {
                    const fd = fieldDefs.find((f) => f.slug === slug);
                    return fd ? (
                      <option key={slug} value={slug}>{fd.name}</option>
                    ) : null;
                  })}
                </select>
              </div>
            )}
          </div>

          {/* Summary card */}
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Summary</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">File</span>
                <span className="text-gray-900 font-medium">{file?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Rows</span>
                <span className="text-gray-900 font-medium">
                  {csvDataRowCount}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Mapped columns</span>
                <span className="text-gray-900 font-medium">{mappedFieldSlugs.length} / {csvHeaders.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Mode</span>
                <span className="text-gray-900 font-medium capitalize">{mode.replace(/_/g, ' ')}</span>
              </div>
            </div>
          </div>

          {importError && (
            <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl border border-red-200">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{importError}</p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <button
              onClick={() => setStep(1)}
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              Back
            </button>
            <button
              disabled={!canImport || importing}
              onClick={handleImport}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-[#0B1120] text-white rounded-lg hover:bg-[#1a2640] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importing…
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Start import
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Results ── */}
      {step === 3 && results && (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C6F135]/20 mb-5">
            <CheckCircle className="w-7 h-7 text-[#0B1120]" />
          </div>
          <h2 className="font-['Satoshi'] text-xl font-bold text-gray-900 mb-1">Import complete</h2>
          <p className="text-sm text-gray-500 mb-8">
            Your records have been imported into <strong>{objectName}</strong>.
          </p>

          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-gray-900">{results.inserted}</p>
              <p className="text-xs text-gray-500 mt-1">Inserted</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-gray-900">{results.updated}</p>
              <p className="text-xs text-gray-500 mt-1">Updated</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-gray-900">{results.skipped}</p>
              <p className="text-xs text-gray-500 mt-1">Skipped</p>
            </div>
          </div>

          {results.errors?.length > 0 && (
            <div className="mb-6 text-left">
              <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                {results.errors.length} row{results.errors.length !== 1 ? 's' : ''} failed
              </p>
              <div className="bg-amber-50 rounded-xl border border-amber-200 divide-y divide-amber-100 max-h-40 overflow-y-auto">
                {results.errors.map((err, i) => (
                  <div key={i} className="px-4 py-2.5 text-xs">
                    <span className="font-medium text-amber-800">Row {err.row}:</span>{' '}
                    <span className="text-amber-700">{err.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-center gap-3">
            <button
              onClick={() => { setStep(0); setFile(null); setCsvHeaders([]); setCsvPreview([]); setCsvDataRowCount(0); setMapping({}); setResults(null); setImportError(''); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Import another file
            </button>
            <button
              onClick={() => navigate(`/data/${objectSlug}`)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-[#0B1120] text-white rounded-lg hover:bg-[#1a2640] transition-colors"
            >
              View {objectName}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
